// @ts-nocheck
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import { Box, Typography, Button, alpha, useTheme } from '@mui/material';
import { ReactComponent as AddIcon } from '../../../../assets/Icons/lucideIcons/plus.svg';
import { ReactComponent as ArrowUpwardIcon } from '../../../../assets/Icons/lucideIcons/move-up.svg';
import { ReactComponent as ArrowDownwardIcon } from '../../../../assets/Icons/lucideIcons/move-down.svg';
import { ReactComponent as DeleteOutlinedIcon } from '../../../../assets/Icons/lucideIcons/trash-2.svg';
import { ReactComponent as EditOutlinedIcon } from '../../../../assets/Icons/lucideIcons/pencil.svg';
import { ReactComponent as VerticalAlignTopIcon } from '../../../../assets/Icons/lucideIcons/arrow-up-to-line.svg';
import { ReactComponent as VerticalAlignBottomIcon } from '../../../../assets/Icons/lucideIcons/arrow-down-to-line.svg';
import { useContextMenu } from 'react-contexify';

import { ContextMenus } from '../../ContextMenus';
import ConfigTableRenderer from '../../ConfigTableRenderer';
import {
  getColumnName,
  formatAttributeValue,
  findUsernameByUserId,
  findGroupNameByGroupId,
} from '../../../../utils/Utils';
import ConfigColumnRowDialog from './ConfigColumnRowDialog';
import ViewDocumentAttribute from '../ViewAttributes/ViewDocumentAttribute';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import swalToast from '../../swalToast';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import REGEX from '../../../../const/Regex';
import { useGroupsQuery } from '../../../../features/administrativeOptions/userManagement/api/UserManagementQueries';

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Parse { Cells: { A1:{value}, B1:{value}, A2:{value}, … } }
 * into rows array: [{ A: val, B: val }, { A: val, B: val }, …]
 */
const parseCellsToRows = (cells, columns) => {
  if (!cells || !columns?.length) return [];
  let maxRow = 0;
  Object.keys(cells).forEach((k) => {
    const m = k.match(REGEX.CELL_REFERENCE);
    if (m) maxRow = Math.max(maxRow, parseInt(m[1], 10));
  });
  const rows = [];
  for (let r = 1; r <= maxRow; r += 1) {
    const row = {};
    columns.forEach((col) => {
      row[col?.colName] = cells[`${col?.colName}${r}`]?.value ?? '';
    });
    rows.push(row);
  }
  return rows;
};

/**
 * Convert rows array back to Cells dict: { A1:{value}, B1:{value}, … }
 */
const rowsToCells = (rows) => {
  const cells = {};
  rows.forEach((row, idx) => {
    const rowNum = idx + 1;
    Object.entries(row).forEach(([colLetter, val]) => {
      cells[`${colLetter}${rowNum}`] = { value: val ?? '' };
    });
  });
  return cells;
};
const USER_DATA_TYPE = 'User';
const GROUP_DATA_TYPE = 'Group';
const MULTI_SELECT_INPUT_CONTROL = 'MULTI_SELECT';

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * ConfigColumnTableView
 *
 * User-facing view for an AdvancedTable attribute that has config_column: true.
 * Renders a structured table with columns = configured attribute schemas.
 * Rows are added/edited via ConfigColumnRowDialog (BasicDetails-powered).
 *
 * Changes remain local until the parent editor explicitly commits them.
 */
const ConfigColumnTableView = forwardRef(
  (
    {
      attribute,
      value,
      onChange,
      isViewMode = false,
      entityData = EMPTY_OBJECT,
      usersList = EMPTY_ARRAY,
      teamUsersList = EMPTY_ARRAY,
      configColumn = false,
      deferCommit = false,
      isUserAuthorizedToOverrideAttribute = false,
    },
    ref
  ) => {
    const theme = useTheme();
    const schema = attribute?.advanced_table_schema || {};
    const advancedTableAttributeId = attribute?.id;

    const { precheckAdvancedTable } = useAttribute();
    const [canRender, setCanRender] = useState(false);
    const [precheckMsg, setPrecheckMsg] = useState('');
    const [isPrechecking, setIsPrechecking] = useState(true);

    // ── Row dialog state ───────────────────────────────────────────────────
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editRowIndex, setEditRowIndex] = useState(null);
    const [insertRowIndex, setInsertRowIndex] = useState(null);
    const hasPrechecked = useRef(false);
    const initCells = useMemo(() => {
      return value?.Cells && Object.keys(value.Cells).length > 0
        ? value.Cells
        : schema?.cells || {};
    }, [value?.Cells, schema?.cells]);

    const initMergedCells = useMemo(() => {
      return value?.mergedCells && value.mergedCells.length > 0
        ? value.mergedCells
        : schema?.mergedCells || [];
    }, [value?.mergedCells, schema?.mergedCells]);

    const initRowSettings = useMemo(() => {
      return value?.rowSettings || {};
    }, [value?.rowSettings]);
    const [rowSettings, setRowSettings] = useState(() => initRowSettings);

    // ── Column definitions ─────────────────────────────────────────────────
    const columns = useMemo(() => {
      const result = [];
      for (let i = 1; i <= (schema?.columns || 0); i += 1) {
        const colName = getColumnName(i);
        const settings = schema?.columnSettings?.[colName];
        if (!settings) continue;
        const config = settings?.attribute_config;
        const apiSchema = config?.attribute_schema || {};
        const width = rowSettings[colName]?.width || settings?.width || 150;
        result.push({
          ...apiSchema,
          colName,
          attrId: config?.attribute_id || apiSchema?.id,
          attributeSchema: apiSchema,
          label: apiSchema?.label || config?.attribute_label || colName,
          width,
          dataType: apiSchema?.data_type || 'Character',
        });
      }
      return result;
    }, [schema, rowSettings]);

    const [rows, setRows] = useState(() =>
      parseCellsToRows(initCells, columns)
    );
    const [mergedCells, setMergedCells] = useState(() => initMergedCells);
    const initialSnapshotRef = useRef({
      rows: cloneDeep(parseCellsToRows(initCells, columns)),
      mergedCells: cloneDeep(initMergedCells),
      rowSettings: cloneDeep(initRowSettings),
    });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const { data: groupList = EMPTY_ARRAY } = useGroupsQuery();

    // Accept externally hydrated values, but preserve edits made in this dialog.
    useEffect(() => {
      if (hasUnsavedChanges) return;
      const nextSnapshot = {
        rows: parseCellsToRows(initCells, columns),
        mergedCells: initMergedCells,
        rowSettings: initRowSettings,
      };
      initialSnapshotRef.current = cloneDeep(nextSnapshot);
      setRows(nextSnapshot.rows);
      setMergedCells(nextSnapshot.mergedCells);
      setRowSettings(nextSnapshot.rowSettings);
    }, [
      initCells,
      initMergedCells,
      initRowSettings,
      columns,
      hasUnsavedChanges,
      setRows,
      setMergedCells,
      setRowSettings,
    ]);

    useEffect(() => {
      if (advancedTableAttributeId && !hasPrechecked.current) {
        hasPrechecked.current = true;
        setIsPrechecking(true);
        precheckAdvancedTable({ attribute_id: advancedTableAttributeId })
          .then((res) => {
            if (res?.data?.can_edit === false) {
              const msg =
                res?.data?.msg ||
                'Column configuration is being updated. Please try again shortly.';
              swalToast({
                icon: 'warning',
                title: msg,
              });
              setPrecheckMsg(msg);
              setCanRender(false);
            } else {
              setCanRender(true);
            }
          })
          .catch(() => {
            setPrecheckMsg('Error verifying table status. Please try again.');
            setCanRender(false);
          })
          .finally(() => {
            setIsPrechecking(false);
          });
      } else if (!advancedTableAttributeId) {
        setCanRender(true);
        setIsPrechecking(false);
      }
    }, [advancedTableAttributeId]);

    // Read as primitives (not the `entityData` object reference) so this
    // callback doesn't get recreated on every render just because a caller
    // passes a freshly-built entityData object.
    const entityId = entityData?.entity_id;
    const entityType = entityData?.entity_type;

    const getDisplayValue = useCallback(
      (val, col) => {
        if (val == null || val === '') {
          return '';
        }
        if (!col?.attributeSchema) {
          return String(val);
        }
        let displayVal = val;
        if (
          col?.attributeSchema?.data_type === USER_DATA_TYPE &&
          col?.attributeSchema?.input_control_type ===
            MULTI_SELECT_INPUT_CONTROL
        ) {
          const userIds = Array.isArray(displayVal) ? displayVal : [displayVal];
          const userNames = userIds
            .map((id) => findUsernameByUserId(id, usersList))
            .filter(Boolean);
          displayVal = userNames.join(', ');
        }
        if (
          col?.attributeSchema?.data_type === GROUP_DATA_TYPE &&
          col?.attributeSchema?.input_control_type ===
            MULTI_SELECT_INPUT_CONTROL
        ) {
          const groupIds = Array.isArray(displayVal)
            ? displayVal
            : [displayVal];
          const groupNames = groupIds
            .map((id) => findGroupNameByGroupId(id, groupList))
            .filter(Boolean);
          displayVal = groupNames.join(', ');
        }
        if (col?.attributeSchema?.data_type === 'Document') {
          return (
            <ViewDocumentAttribute
              documentDetails={displayVal}
              attribute={col?.attributeSchema}
              configColumn={true}
              entityId={entityId}
              entityType={entityType}
            />
          );
        }

        const result = formatAttributeValue(
          displayVal,
          col?.attributeSchema,
          false,
          null,
          {},
          usersList,
          groupList,
          null,
          {},
          isViewMode,
          configColumn
        );
        if (result === '-') {
          return '';
        }
        return result;
      },
      [usersList, groupList, isViewMode, configColumn, entityId, entityType]
    );

    const handleAddRowClick = useCallback(() => {
      setEditRowIndex(null);
      setInsertRowIndex(null);
      setDialogOpen(true);
    }, [setEditRowIndex, setInsertRowIndex, setDialogOpen]);

    const handleRowDialogClose = useCallback(() => {
      setDialogOpen(false);
      setEditRowIndex(null);
      setInsertRowIndex(null);
    }, [setDialogOpen, setEditRowIndex, setInsertRowIndex]);

    const { show: showContextMenu } = useContextMenu();

    // ── Right-click context menu ───────────────────────────────────────────
    const [menuRowIndex, setMenuRowIndex] = useState(null);

    // ── Emit onChange with updated cells ──────────────────────────────────
    const getTableValue = useCallback(
      (
        currentRows = rows,
        currentMergedCells = mergedCells,
        currentRowSettings = rowSettings
      ) => ({
        ...(value || {}),
        Cells: rowsToCells(currentRows),
        mergedCells: currentMergedCells,
        rowSettings: currentRowSettings,
      }),
      [rows, mergedCells, rowSettings, value]
    );

    const commitTable = useCallback(
      (
        currentRows = rows,
        currentMergedCells = mergedCells,
        currentRowSettings = rowSettings
      ) => {
        const nextValue = getTableValue(
          currentRows,
          currentMergedCells,
          currentRowSettings
        );
        onChange(nextValue);
        initialSnapshotRef.current = cloneDeep({
          rows: currentRows,
          mergedCells: currentMergedCells,
          rowSettings: currentRowSettings,
        });
        setHasUnsavedChanges(false);
        return nextValue;
      },
      [getTableValue, onChange, rows, mergedCells, rowSettings]
    );

    const discardTableChanges = useCallback(() => {
      const snapshot = cloneDeep(initialSnapshotRef.current);
      setRows(snapshot.rows);
      setMergedCells(snapshot.mergedCells);
      setRowSettings(snapshot.rowSettings);
      setHasUnsavedChanges(false);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        commit: () => commitTable(),
        discard: () => discardTableChanges(),
      }),
      [commitTable, discardTableChanges]
    );

    const handleResizeColumn = useCallback(
      (colName, newWidth) => {
        setRowSettings((prev) => {
          const newRowSettings = {
            ...prev,
            [colName]: {
              ...(prev[colName] || {}),
              width: newWidth,
            },
          };
          if (deferCommit) {
            setHasUnsavedChanges(true);
          } else {
            commitTable(rows, mergedCells, newRowSettings);
          }
          return newRowSettings;
        });
      },
      [deferCommit, commitTable, rows, mergedCells, setRowSettings]
    );

    // ── Row add handler ────────────────────────────────────────────────────
    const handleAddRow = useCallback(
      ({ rowType, values: rowValues, customText }) => {
        let newRows = [...rows];
        let newMergedCells = [...mergedCells];
        const targetIndex =
          insertRowIndex != null ? insertRowIndex : rows?.length;
        const newRowNum = targetIndex + 1;

        let newRow = {};
        if (rowType === 'custom') {
          // Custom row — store text in A column; add merge entry
          columns.forEach((col, idx) => {
            newRow[col.colName] = idx === 0 ? customText : '';
          });

          // Shift existing merged cells down
          newMergedCells = newMergedCells.map((mc) => {
            if (mc?.row >= newRowNum) return { ...mc, row: mc?.row + 1 };
            return mc;
          });

          newMergedCells.push({
            row: newRowNum,
            startCol: 1,
            endCol: columns.length,
          });
        } else {
          // Default row — map col-letter values to a row object
          columns.forEach((col) => {
            newRow[col.colName] = rowValues?.[col.colName] ?? '';
          });

          // Shift existing merged cells down
          newMergedCells = newMergedCells.map((mc) => {
            if (mc?.row >= newRowNum) return { ...mc, row: mc?.row + 1 };
            return mc;
          });
        }

        newRows.splice(targetIndex, 0, newRow);

        setRows(newRows);
        setMergedCells(newMergedCells);
        setDialogOpen(false);
        setEditRowIndex(null);
        setInsertRowIndex(null);

        if (deferCommit) {
          setHasUnsavedChanges(true);
        } else {
          commitTable(newRows, newMergedCells, rowSettings);
        }
      },
      [rows, mergedCells, columns, deferCommit, commitTable, rowSettings]
    );

    // ── Row edit handler ───────────────────────────────────────────────────
    const handleEditRow = useCallback(
      ({ rowType, values: rowValues, customText }) => {
        if (editRowIndex == null) return;
        const rowNum = editRowIndex + 1; // 1-based
        const newRows = rows.map((r, idx) => {
          if (idx !== editRowIndex) return r;
          if (rowType === 'custom') {
            const updated = {};
            columns.forEach((col, ci) => {
              updated[col.colName] = ci === 0 ? customText : '';
            });
            return updated;
          }
          const updated = {};
          columns.forEach((col) => {
            updated[col.colName] = rowValues?.[col.colName] ?? '';
          });
          return updated;
        });

        let newMergedCells = mergedCells.filter((mc) => mc.row !== rowNum);
        if (rowType === 'custom') {
          newMergedCells.push({
            row: rowNum,
            startCol: 1,
            endCol: columns.length,
          });
        }

        setRows(newRows);
        setMergedCells(newMergedCells);
        setDialogOpen(false);
        setEditRowIndex(null);

        if (deferCommit) {
          setHasUnsavedChanges(true);
        } else {
          commitTable(newRows, newMergedCells, rowSettings);
        }
      },
      [
        editRowIndex,
        rows,
        mergedCells,
        columns,
        setDialogOpen,
        setEditRowIndex,
        setRows,
        setMergedCells,
        deferCommit,
        commitTable,
        rowSettings,
      ]
    );

    // ── Context menu actions ───────────────────────────────────────────────
    const openMenu = useCallback(
      (e, rowIdx) => {
        e.preventDefault();
        e.stopPropagation();
        setMenuRowIndex(rowIdx);
        showContextMenu({
          id: `config-column-table-menu-${advancedTableAttributeId}`,
          event: e,
          props: rowIdx,
        });
      },
      [showContextMenu, advancedTableAttributeId, setMenuRowIndex]
    );

    const closeMenu = useCallback(() => {
      setMenuRowIndex(null);
    }, [setMenuRowIndex]);

    const handleMenuEdit = useCallback(() => {
      setEditRowIndex(menuRowIndex);
      setInsertRowIndex(null);
      setDialogOpen(true);
      closeMenu();
    }, [
      menuRowIndex,
      closeMenu,
      setEditRowIndex,
      setInsertRowIndex,
      setDialogOpen,
    ]);

    const handleMenuAddUp = useCallback(() => {
      setInsertRowIndex(menuRowIndex);
      setEditRowIndex(null);
      setDialogOpen(true);
      closeMenu();
    }, [
      menuRowIndex,
      closeMenu,
      setInsertRowIndex,
      setEditRowIndex,
      setDialogOpen,
    ]);

    const handleMenuAddDown = useCallback(() => {
      setInsertRowIndex(menuRowIndex + 1);
      setEditRowIndex(null);
      setDialogOpen(true);
      closeMenu();
    }, [
      menuRowIndex,
      closeMenu,
      setInsertRowIndex,
      setEditRowIndex,
      setDialogOpen,
    ]);

    const handleMenuDelete = useCallback(() => {
      const rowNum = menuRowIndex + 1; // 1-based
      const newRows = rows.filter((_, idx) => idx !== menuRowIndex);
      // Shift merged cell row numbers
      const newMergedCells = mergedCells
        .filter((mc) => mc.row !== rowNum)
        .map((mc) => (mc.row > rowNum ? { ...mc, row: mc.row - 1 } : mc));
      setRows(newRows);
      setMergedCells(newMergedCells);
      closeMenu();

      if (deferCommit) {
        setHasUnsavedChanges(true);
      } else {
        commitTable(newRows, newMergedCells, rowSettings);
      }
    }, [
      menuRowIndex,
      rows,
      mergedCells,
      closeMenu,
      setRows,
      setMergedCells,
      deferCommit,
      commitTable,
      rowSettings,
    ]);

    const handleMenuMoveUp = useCallback(() => {
      const idx = menuRowIndex;
      if (idx <= 0) return;
      const newRows = [...rows];
      [newRows[idx - 1], newRows[idx]] = [newRows[idx], newRows[idx - 1]];
      // Swap merged-cell entries
      const rowA = idx + 1; // 1-based
      const rowB = idx;
      const newMergedCells = mergedCells.map((mc) => {
        if (mc.row === rowA) return { ...mc, row: rowB };
        if (mc.row === rowB) return { ...mc, row: rowA };
        return mc;
      });
      setRows(newRows);
      setMergedCells(newMergedCells);
      closeMenu();

      if (deferCommit) {
        setHasUnsavedChanges(true);
      } else {
        commitTable(newRows, newMergedCells, rowSettings);
      }
    }, [
      menuRowIndex,
      rows,
      mergedCells,
      closeMenu,
      setRows,
      setMergedCells,
      deferCommit,
      commitTable,
      rowSettings,
    ]);

    const handleMenuMoveDown = useCallback(() => {
      const idx = menuRowIndex;
      if (idx >= rows.length - 1) return;
      const newRows = [...rows];
      [newRows[idx], newRows[idx + 1]] = [newRows[idx + 1], newRows[idx]];
      const rowA = idx + 1;
      const rowB = idx + 2;
      const newMergedCells = mergedCells.map((mc) => {
        if (mc.row === rowA) return { ...mc, row: rowB };
        if (mc.row === rowB) return { ...mc, row: rowA };
        return mc;
      });
      setRows(newRows);
      setMergedCells(newMergedCells);
      closeMenu();

      if (deferCommit) {
        setHasUnsavedChanges(true);
      } else {
        commitTable(newRows, newMergedCells, rowSettings);
      }
    }, [
      menuRowIndex,
      rows,
      mergedCells,
      closeMenu,
      setRows,
      setMergedCells,
      deferCommit,
      commitTable,
      rowSettings,
    ]);

    const menus = useMemo(() => {
      return [
        {
          label: 'Edit',
          id: 'edit',
          icon: EditOutlinedIcon,
          onClick: () => {
            setTimeout(() => handleMenuEdit(), 0);
          },
        },
        {
          label: 'Add Row Above',
          id: 'addUp',
          icon: VerticalAlignTopIcon,
          onClick: () => {
            setTimeout(() => handleMenuAddUp(), 0);
          },
        },
        {
          label: 'Add Row Below',
          id: 'addDown',
          icon: VerticalAlignBottomIcon,
          onClick: () => {
            setTimeout(() => handleMenuAddDown(), 0);
          },
        },
        {
          label: 'Move Up',
          id: 'moveUp',
          icon: ArrowUpwardIcon,
          isMenuDisabled: () => menuRowIndex === 0,
          onClick: () => {
            setTimeout(() => handleMenuMoveUp(), 0);
          },
        },
        {
          label: 'Move Down',
          id: 'moveDown',
          icon: ArrowDownwardIcon,
          isMenuDisabled: () => menuRowIndex === rows.length - 1,
          onClick: () => {
            setTimeout(() => handleMenuMoveDown(), 0);
          },
        },
        {
          label: 'Delete',
          id: 'delete',
          icon: DeleteOutlinedIcon,
          onClick: () => {
            setTimeout(() => handleMenuDelete(), 0);
          },
        },
      ];
    }, [
      handleMenuEdit,
      handleMenuAddUp,
      handleMenuAddDown,
      handleMenuMoveUp,
      handleMenuMoveDown,
      handleMenuDelete,
      menuRowIndex,
      rows.length,
    ]);

    // ── Derive edit props for dialog ──────────────────────────────────────
    const isMergedRow = useCallback(
      (idx) => {
        const rowNum = idx + 1;
        return mergedCells.some((mc) => mc.row === rowNum);
      },
      [mergedCells]
    );

    const editRowValues = useMemo(() => {
      if (editRowIndex == null) return null;
      if (isMergedRow(editRowIndex)) return null; // custom row
      return rows[editRowIndex] || null;
    }, [editRowIndex, rows, isMergedRow]);

    const editRowType = useMemo(() => {
      if (editRowIndex == null) return 'default';
      return isMergedRow(editRowIndex) ? 'custom' : 'default';
    }, [editRowIndex, isMergedRow]);

    const editCustomTextValue = useMemo(() => {
      if (editRowIndex == null || !isMergedRow(editRowIndex)) return '';
      const firstColName = columns[0]?.colName || 'A';
      return rows[editRowIndex]?.[firstColName] ?? '';
    }, [editRowIndex, isMergedRow, rows, columns]);

    const hasColumns = columns.length > 0;

    const rowNumber = (() => {
      if (editRowIndex !== null) return editRowIndex + 1;
      if (insertRowIndex !== null) return insertRowIndex + 1;
      return rows?.length + 1;
    })();

    // ── Render ────────────────────────────────────────────────────────────
    if (isPrechecking) {
      return (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading table configuration...
          </Typography>
        </Box>
      );
    }

    if (!canRender) {
      return (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="body1">
            {precheckMsg || 'Table access is currently restricted.'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%' }}>
        {/* ── Header bar ── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mb: 1.5,
            px: 0.5,
          }}
        >
          {!isViewMode && hasColumns && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddRowClick}
            >
              Add Row
            </Button>
          )}
        </Box>

        {/* ── Table ── */}
        {!hasColumns ? (
          <Box
            sx={{
              border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">
              No columns configured for this table.
            </Typography>
          </Box>
        ) : (
          <ConfigTableRenderer
            columns={columns}
            rows={rows}
            isViewMode={isViewMode}
            handleAddRowClick={handleAddRowClick}
            openMenu={openMenu}
            isMergedRow={isMergedRow}
            getDisplayValue={getDisplayValue}
            handleResizeColumn={handleResizeColumn}
          />
        )}

        {/* ── Context / action menu ── */}
        <ContextMenus
          menus={menus}
          menuId={`config-column-table-menu-${advancedTableAttributeId}`}
          onMenuExit={closeMenu}
        />

        {/* ── Row dialog ── */}
        {dialogOpen && (
          <ConfigColumnRowDialog
            open={dialogOpen}
            onClose={handleRowDialogClose}
            columns={columns}
            entityData={entityData}
            advancedTableAttributeId={advancedTableAttributeId}
            editRowValues={editRowValues}
            rowNumber={rowNumber}
            rowType={editRowType}
            editCustomText={editCustomTextValue}
            usersList={usersList}
            teamUsersList={teamUsersList}
            onSave={editRowIndex != null ? handleEditRow : handleAddRow}
            configColumn={configColumn}
            isUserAuthorizedToOverrideAttribute={
              isUserAuthorizedToOverrideAttribute
            }
          />
        )}
      </Box>
    );
  }
);

ConfigColumnTableView.propTypes = {
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    label: PropTypes.string,
    advanced_table_schema: PropTypes.shape({
      columns: PropTypes.number,
      columnSettings: PropTypes.shape({}),
    }),
  }).isRequired,
  value: PropTypes.shape({
    Cells: PropTypes.shape({}),
    mergedCells: PropTypes.arrayOf(PropTypes.shape({})),
    rowSettings: PropTypes.shape({}),
  }),
  onChange: PropTypes.func.isRequired,
  isViewMode: PropTypes.bool,
  entityData: PropTypes.shape({
    entity_id: PropTypes.string,
    entity_type: PropTypes.string,
  }),
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  configColumn: PropTypes.bool,
  deferCommit: PropTypes.bool,
  isUserAuthorizedToOverrideAttribute: PropTypes.bool,
};

export default ConfigColumnTableView;
